/**
 * Environment variable validation utility
 * Validates all required environment variables on app startup
 */

import { createLogger } from './logger';

const logger = createLogger('validateEnv');

interface EnvConfig {
  name: string;
  required: boolean;
  validator?: (value: string) => boolean;
  errorMessage?: string;
}

const requiredEnvVars: EnvConfig[] = [
  {
    name: 'REACT_APP_APIKEY',
    required: true,
    errorMessage: 'Firebase API key is required',
  },
  {
    name: 'REACT_APP_AUTH_DOMAIN',
    required: true,
    errorMessage: 'Firebase Auth Domain is required',
  },
  {
    name: 'REACT_APP_PROJECT_ID',
    required: true,
    errorMessage: 'Firebase Project ID is required',
  },
  {
    name: 'REACT_APP_STORAGE_BUCKET',
    required: true,
    errorMessage: 'Firebase Storage Bucket is required',
  },
  {
    name: 'REACT_APP_MESSAGING_SENDER_ID',
    required: true,
    errorMessage: 'Firebase Messaging Sender ID is required',
  },
  {
    name: 'REACT_APP_APP_ID',
    required: true,
    errorMessage: 'Firebase App ID is required',
  },
  {
    name: 'REACT_APP_MEASUREMENT_ID',
    required: false,
  },
  {
    name: 'REACT_APP_VAPID_KEY',
    required: false,
  },
  {
    name: 'REACT_APP_FCM_VAPID_KEY',
    required: false,
  },
  {
    name: 'REACT_APP_PAYMENT_TYPE',
    required: false,
  },
  {
    name: 'REACT_APP_CONFIRMATION_FEE',
    required: false,
    validator: (value: string) => !isNaN(parseFloat(value)),
    errorMessage: 'Confirmation fee must be a valid number',
  },
  {
    name: 'REACT_APP_PAYMENT_GATEWAY',
    required: false,
  },
  {
    name: 'REACT_APP_PAYMENT_METHOD',
    required: false,
  },
  {
    name: 'REACT_APP_WIPAY_RESPONSE_URL',
    required: false,
    validator: (value: string) => {
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    },
    errorMessage: 'WiPay response URL must be a valid URL',
  },
];

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Validates all required environment variables
 * @returns ValidationResult with validation status and messages
 */
export const validateEnvironmentVariables = (): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  requiredEnvVars.forEach(({ name, required, validator, errorMessage }) => {
    const value = process.env[name];

    // Check if required variable is missing
    if (required && (!value || value.trim() === '')) {
      errors.push(errorMessage || `Required environment variable ${name} is missing or empty`);
      return;
    }

    // Check if optional variable is missing (warning only)
    if (!required && (!value || value.trim() === '')) {
      warnings.push(`Optional environment variable ${name} is not set`);
      return;
    }

    // Run custom validator if provided and value exists
    if (value && validator && !validator(value)) {
      errors.push(errorMessage || `Environment variable ${name} has an invalid value`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Validates environment variables and throws error if validation fails
 * Logs warnings for optional missing variables
 */
export const validateEnvOrThrow = (): void => {
  const result = validateEnvironmentVariables();

  // Log warnings in development
  if (result.warnings.length > 0 && process.env.NODE_ENV === 'development') {
    result.warnings.forEach((warning) => {
      logger.warn(warning);
    });
  }

  // Throw error if validation fails
  if (!result.isValid) {
    const errorMessage = [
      '❌ Environment Variable Validation Failed',
      '',
      'The following required environment variables are missing or invalid:',
      ...result.errors.map((err) => `  • ${err}`),
      '',
      'Please ensure all required environment variables are set in your .env.local file.',
      'See README.md or docs/DEPLOYMENT.md for setup instructions.',
    ].join('\n');

    throw new Error(errorMessage);
  }
};

/**
 * Gets a validated environment variable value
 * @param name - Environment variable name
 * @param defaultValue - Optional default value if variable is not set
 * @returns The environment variable value or default
 */
export const getEnvVar = (name: string, defaultValue?: string): string => {
  const value = process.env[name];

  if (!value && defaultValue !== undefined) {
    return defaultValue;
  }

  if (!value) {
    throw new Error(`Environment variable ${name} is not set`);
  }

  return value;
};

/**
 * Checks if app is running in development mode
 */
export const isDevelopment = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

/**
 * Checks if app is running in production mode
 */
export const isProduction = (): boolean => {
  return process.env.NODE_ENV === 'production';
};

/**
 * Checks if app is running in test mode
 */
export const isTest = (): boolean => {
  return process.env.NODE_ENV === 'test';
};
